import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Archive, Zap, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/UserAvatar";
import x2gems from "@/assets/boostx2gems.png";

interface InventoryItem {
    id: string;
    is_active: boolean;
    item_id: string;
    acquired_at: string;
    shop_items: {
        name: string;
        description: string;
        type: 'boost' | 'border';
        metadata: any;
    };
}

const Inventory = () => {
    const { user } = useAuth();
    const [items, setItems] = useState<InventoryItem[]>([]);
    const [profile, setProfile] = useState<{ avatar_url?: string; display_name?: string; boost_end_date?: string } | null>(null);
    const [loading, setLoading] = useState(true);
    const [activating, setActivating] = useState<string | null>(null);

    const fetchInventory = async () => {
        if (!user) return;

        // Fetch profile for avatar/fallback and boost info
        const { data: profileData } = await supabase
            .from('profiles')
            .select('avatar_url, display_name, boost_end_date')
            .eq('id', user.id)
            .single();

        if (profileData) setProfile(profileData);

        const { data, error } = await supabase
            .from('user_inventory')
            .select('*, shop_items(*)')
            .eq('user_id', user.id);

        if (error) {
            console.error('Error fetching inventory:', error);
        } else {
            setItems(data as any[]);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchInventory();
    }, [user]);

    const handleActivate = async (item: InventoryItem) => {
        if (!user || item.is_active) return;

        setActivating(item.id);
        try {
            if (item.shop_items.type === 'border') {
                const { error } = await supabase.rpc('activate_user_border', {
                    p_item_id: item.item_id
                });

                if (error) {
                    toast.error(error.message);
                } else {
                    toast.success(`${item.shop_items.name} activated!`);
                    fetchInventory();
                    // Trigger global refresh for header/menu
                    window.dispatchEvent(new Event('profile-updated'));
                }
            } else {
                toast.info("Active boosts are applied automatically.");
            }
        } catch (error) {
            console.error('Error activating item:', error);
            toast.error("An unexpected error occurred");
        } finally {
            setActivating(null);
        }
    };

    const borders = items.filter(i => i.shop_items.type === 'border');
    const boosts = items.filter(i => i.shop_items.type === 'boost');

    return (
        <Layout>
            <main className="container mx-auto px-4 py-8">
                <div className="flex flex-col gap-8">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                                <Archive className="w-8 h-8 text-primary" />
                                Your Inventory
                            </h1>
                            <p className="text-muted-foreground">Manage your purchased items and boosts.</p>
                        </div>
                        <Button variant="outline" onClick={() => window.location.href = '/shop'} className="rounded-xl">
                            Visit Shop
                        </Button>
                    </div>

                    <section className="space-y-6">
                        <h2 className="text-xl font-bold flex items-center gap-2 text-primary">
                            Collected Borders
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                            {loading ? (
                                Array(4).fill(0).map((_, i) => <div key={i} className="h-48 bg-card/50 animate-pulse rounded-2xl" />)
                            ) : (
                                <>
                                    {/* Blank Border Option */}
                                    <Card className={cn(
                                        "glass-card border-white/5 transition-all overflow-hidden flex flex-col justify-between rounded-custom-paddings",
                                        !items.some(i => i.shop_items.type === 'border' && i.is_active) && "border-primary/50 ring-1 ring-primary/20 shadow-lg"
                                    )}>
                                        <div className="rounded-custom-paddings bg-gray-800 mb-4">
                                            <CardHeader className="p-4 pb-0  pt-2">
                                                <div className="flex items-center justify-between">
                                                    <CardTitle className="text-md text-center w-full">None</CardTitle>
                                                    {!items.some(i => i.shop_items.type === 'border' && i.is_active) && <CheckCircle2 className="w-5 h-5 text-primary" />}
                                                </div>
                                            </CardHeader>
                                            <CardContent className="p-4 pt-2">
                                                <div className="h-20 flex items-center justify-center mb-2">
                                                    <UserAvatar
                                                        size="lg"
                                                        src={profile?.avatar_url}
                                                        fallback={profile?.display_name?.charAt(0) || 'U'}
                                                    />
                                                </div>
                                                <p className="text-xs text-muted-foreground text-center line-clamp-2">Remove any active border style.</p>
                                            </CardContent>
                                        </div>
                                        <CardFooter className="px-4 flex flex-row justify-center pb-2">
                                            <Button
                                                variant={!items.some(i => i.shop_items.type === 'border' && i.is_active) ? "ghost" : "secondary"}
                                                disabled={!items.some(i => i.shop_items.type === 'border' && i.is_active) || activating === 'none'}
                                                onClick={async () => {
                                                    setActivating('none');
                                                    try {
                                                        const { error } = await supabase.rpc('activate_user_border', { p_item_id: null });
                                                        if (error) toast.error(error.message);
                                                        else {
                                                            toast.success("Border removed");
                                                            fetchInventory();
                                                            // Trigger global refresh for header/menu
                                                            window.dispatchEvent(new Event('profile-updated'));
                                                        }
                                                    } catch (err) {
                                                        console.error(err);
                                                        toast.error("Failed to remove border");
                                                    } finally {
                                                        setActivating(null);
                                                    }
                                                }}
                                                className="w-full text-xs rounded-lg h-8 w-fit"
                                            >
                                                {!items.some(i => i.shop_items.type === 'border' && i.is_active) ? "Active" : activating === 'none' ? "..." : "Activate"}
                                            </Button>
                                        </CardFooter>
                                    </Card>

                                    {borders.map(item => (
                                        <Card key={item.id} className={cn(
                                            "glass-card border-white/5 transition-all overflow-hidden flex flex-col justify-between rounded-custom-paddings",
                                            item.is_active && "border-primary/50 ring-1 ring-primary/20 shadow-lg"
                                        )}>
                                            <div className="rounded-custom-paddings bg-gray-800 mb-2">
                                                <CardHeader className="p-4 pb-0 pt-2">
                                                    <div className="flex items-center justify-between">
                                                        <CardTitle className="text-md text-center w-full">{item.shop_items.name}</CardTitle>
                                                        {item.is_active && <CheckCircle2 className="w-5 h-5 text-primary" />}
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="p-4 pt-2">
                                                    <div className="h-20 flex items-center justify-center mb-2">
                                                        <UserAvatar
                                                            size="lg"
                                                            src={profile?.avatar_url}
                                                            borderStyle={item.shop_items.metadata.style}
                                                            fallback={profile?.display_name?.charAt(0) || 'U'}
                                                        />
                                                    </div>
                                                    <p className="text-xs text-muted-foreground text-center line-clamp-2">{item.shop_items.description}</p>
                                                </CardContent>
                                            </div>
                                            <CardFooter className="px-4 flex flex-row justify-center pb-2">
                                                <Button
                                                    variant={item.is_active ? "ghost" : ((item.shop_items.metadata.style === 'beta' || item.shop_items.metadata.style === 'neon' || item.shop_items.metadata.style === 'gold') ? "default" : "secondary")}
                                                    disabled={item.is_active || activating === item.id}
                                                    onClick={() => handleActivate(item)}
                                                    className={cn(
                                                        "w-full text-xs rounded-lg h-8 w-fit",
                                                        !item.is_active && (item.shop_items.metadata.style === 'beta' || item.shop_items.metadata.style === 'neon' || item.shop_items.metadata.style === 'gold') && "bg-purple-500 hover:bg-purple-600 border-none shadow-[0_0_10px_rgba(168,85,247,0.3)]"
                                                    )}
                                                >
                                                    {item.is_active ? "Active" : activating === item.id ? "..." : "Activate"}
                                                </Button>
                                            </CardFooter>
                                        </Card>
                                    ))}
                                </>
                            )}
                        </div>
                    </section>

                    <section className="space-y-6">
                        <h2 className="text-xl font-bold flex items-center gap-2 text-yellow-500">
                            <Zap className="w-6 h-6 fill-yellow-500/20" />
                            Gems Boosts
                        </h2>

                        {loading ? (
                            <div className="h-32 bg-card/50 rounded-2xl" />
                        ) : (() => {
                            const sortedBoosts = [...boosts].sort((a, b) => new Date(b.acquired_at).getTime() - new Date(a.acquired_at).getTime());
                            const activeBoost = sortedBoosts.find(b => b.is_active) ||
                                sortedBoosts.find(b => {
                                    const endDate = new Date(new Date(b.acquired_at).getTime() + (b.shop_items.metadata?.duration_days * 24 * 60 * 60 * 1000));
                                    return endDate.getTime() > new Date().getTime();
                                });
                            const historyBoosts = sortedBoosts.filter(b => b.id !== activeBoost?.id);

                            return (
                                <div className="flex flex-col gap-6">
                                    {/* Active Boost Highlight */}
                                    {activeBoost ? (
                                        <div className="relative group">
                                            <div className="relative flex flex-col md:flex-row items-center gap-6 p-6 rounded-2xl bg-gradient-to-br from-gray-900 via-gray-900 to-yellow-500/10  overflow-hidden">
                                                <Sparkles className="absolute -right-4 -top-4 w-24 h-24 text-yellow-500/5 rotate-12" />

                                                <div className="relative">
                                                    <div className="absolute -inset-4 bg-yellow-500/10 rounded-full blur-2xl" />
                                                    <img src={x2gems} alt="x2gems" className="w-20 h-20 relative z-10 animate-bounce [animation-duration:3s]" />
                                                </div>

                                                <div className="flex-1 text-center md:text-left z-10">
                                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-4">
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-1 justify-center md:justify-start">
                                                                <span className="text-[10px] bg-yellow-500 text-black px-2 py-0.5 rounded-full font-bold uppercase tracking-wider shadow-[0_0_10px_rgba(234,179,8,0.5)]">
                                                                    ACTIVE NOW
                                                                </span>
                                                                <h3 className="text-2xl font-bold text-yellow-500 drop-shadow-sm">
                                                                    {activeBoost.shop_items.name}
                                                                </h3>
                                                            </div>
                                                            <p className="text-muted-foreground">{activeBoost.shop_items.description}</p>
                                                        </div>
                                                        <div className="bg-black/40 backdrop-blur-sm border border-white/5 p-3 rounded-xl min-w-[160px]">
                                                            <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest mb-1">Time Remaining</p>
                                                            <div className="flex items-center gap-2 text-yellow-500">
                                                                <span className="font-mono text-lg font-bold">
                                                                    {activeBoost.shop_items.metadata?.duration_days && (
                                                                        (() => {
                                                                            const endDate = new Date(new Date(activeBoost.acquired_at).getTime() + (activeBoost.shop_items.metadata.duration_days * 24 * 60 * 60 * 1000));
                                                                            const diff = endDate.getTime() - new Date().getTime();
                                                                            const days = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
                                                                            return `${days} Days Left`;
                                                                        })()
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-6 text-sm py-3 border-t border-white/5">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-muted-foreground uppercase">Started</span>
                                                            <span className="font-medium">{new Date(activeBoost.acquired_at).toLocaleDateString()}</span>
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-muted-foreground uppercase">Expires</span>
                                                            <span className="font-medium text-yellow-500/80">
                                                                {activeBoost.shop_items.metadata?.duration_days &&
                                                                    (() => {
                                                                        const itemDurationMs = activeBoost.shop_items.metadata.duration_days * 24 * 60 * 60 * 1000;
                                                                        const itemEndDate = new Date(new Date(activeBoost.acquired_at).getTime() + itemDurationMs);

                                                                        // Check actual profile end date
                                                                        const realEndDate = profile?.boost_end_date ? new Date(profile.boost_end_date) : itemEndDate;

                                                                        // If real end date is significantly later (> 1 hour) than item end date
                                                                        const diffMs = realEndDate.getTime() - itemEndDate.getTime();
                                                                        const hasExtra = diffMs > 1000 * 60 * 60;

                                                                        return (
                                                                            <span className="flex items-center gap-1">
                                                                                {itemEndDate.toLocaleDateString()}
                                                                                {hasExtra && (
                                                                                    <span className="text-[10px] text-green-400 font-bold ml-1 animate-pulse">
                                                                                        (+ {Math.ceil(diffMs / (1000 * 60 * 60 * 24))} Day Boost)
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                        );
                                                                    })()
                                                                }
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-8 rounded-2xl bg-card/20 border border-dashed border-white/5 text-center">
                                            <p className="text-muted-foreground italic">No active boost at the moment.</p>
                                            <Button variant="link" onClick={() => window.location.href = '/shop'} className="text-yellow-500/50 hover:text-yellow-500">
                                                Get a Gem Boost in the shop →
                                            </Button>
                                        </div>
                                    )}

                                    {/* Boost History Section */}
                                    {historyBoosts.length > 0 && (
                                        <div className="space-y-4 pt-4">
                                            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-widest px-1">
                                                Boost History
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 opacity-60">
                                                {historyBoosts.sort((a, b) => new Date(b.acquired_at).getTime() - new Date(a.acquired_at).getTime()).map(item => {
                                                    const endDate = new Date(new Date(item.acquired_at).getTime() + (item.shop_items.metadata?.duration_days * 24 * 60 * 60 * 1000));
                                                    const isFuture = endDate.getTime() > new Date().getTime();

                                                    return (
                                                        <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl bg-card/40 border border-white/5 grayscale hover:grayscale-0 transition-all duration-300">
                                                            <img src={x2gems} alt="x2gems" className="w-10 h-10 opacity-50" />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <h4 className="font-medium truncate text-sm">{item.shop_items.name}</h4>
                                                                    <span className={cn(
                                                                        "text-[9px] whitespace-nowrap",
                                                                        isFuture ? "text-yellow-500/80 font-bold" : "text-muted-foreground"
                                                                    )}>
                                                                        {isFuture ? "Expires" : "Ended"} {endDate.toLocaleDateString()}
                                                                    </span>
                                                                </div>
                                                                <p className="text-[10px] text-muted-foreground italic truncate">
                                                                    Purchased on {new Date(item.acquired_at).toLocaleDateString()}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </section>
                </div>
            </main>
        </Layout>
    );
};

export default Inventory;
